// Functional utility methods to simplify the devices code.

import type { MicrophoneId } from '../media/media.types';
const defaultTypes = new Set(['default', 'communications']);

/**
 * Find a device with the 'default' label,
 * else the first device of a certain type.
 *
 * @param devices MediaDeviceInfo[] to find within.
 * @param mediaKind MediaDeviceKind - filtered by one of the following "audioinput" | "audiooutput" | "videoinput"
 * @returns ID of device
 */
export const findDefaultDevice = (
  devices: MediaDeviceInfo[],
  mediaKind: MediaDeviceKind,
): string | undefined => {
  const devicesByKind = devices.filter((device) => device.kind === mediaKind);

  const defaultDevice = devicesByKind.find((device) =>
    device.deviceId.toLowerCase().includes('default'),
  );

  return defaultDevice ? defaultDevice.deviceId : devicesByKind[0]?.deviceId;
};

/**
 * Does a device with this ID exist in the devices array
 * @param id device ID
 * @param devices Array of MediaDeviceInfo
 * @returns boolean - if the device exists
 */
export const hasDevice = (id: string, devices: MediaDeviceInfo[]): boolean =>
  devices.some(({ deviceId }) => id === deviceId);

export const findDeviceById = (
  id: string | undefined,
  devices: MediaDeviceInfo[],
  mediaKind: MediaDeviceKind,
): MediaDeviceInfo | undefined =>
  devices.find((el) => id === el.deviceId && el.kind === mediaKind);

/**
 *
 * @param id device ID
 * @param devices Array of MediaDeviceInfo
 * @returns boolean
 */
export const shouldKeepDeviceSelected = (
  id: string | undefined,
  devices: MediaDeviceInfo[],
): boolean => {
  return id !== undefined && hasDevice(id, devices);
};

/**
 * Used to differentiate between default devices. Tests if the new device is the same as
 * the previous selected one, based on id and group
 * @param newDevice
 * @param currentDeviceid
 * @param currentGroupId
 * @returns {boolean}
 */
export const isDifferentMic = (
  newDevice: MediaDeviceInfo | undefined,
  currentDeviceid?: MicrophoneId,
  currentGroupId?: string,
): boolean => {
  if (!newDevice) {
    return false;
  }
  if (!currentDeviceid) {
    return true;
  }

  if (
    defaultTypes.has(newDevice.deviceId) &&
    defaultTypes.has(currentDeviceid) &&
    currentGroupId === newDevice.groupId
  ) {
    return false;
  } else if (
    !defaultTypes.has(newDevice.deviceId) &&
    newDevice.deviceId === currentDeviceid
  ) {
    return false;
  }
  return true;
};

/**
 * Chrome uses default devices, so implicit device selection can be made.
 * Call providers do not always play nicely with default devices, so we allow
 * the explicit device id to be retrieved.
 *
 * @returns MediaDevice or undefined
 */
export const getExplicitIdForGroup = (
  device: MediaDeviceInfo | undefined,
  devices: MediaDeviceInfo[],
  mediaKind: MediaDeviceKind,
): MediaDeviceInfo | undefined => {
  return devices.find((el) => {
    return (
      el.groupId === device?.groupId &&
      el.kind === mediaKind &&
      !defaultTypes.has(el.deviceId)
    );
  });
};
